import { Button } from '@/components/ui/button';
import { toast } from '@/hooks/use-toast';
import {
    revokeAllsessionsMutationFn,
    revokeSessionMutationFn,
    sessionsQueryFn,
} from '@/lib/api';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Loader } from 'lucide-react';
import { useCallback, useEffect } from 'react';
import SessionItem from './session-item';

const Sessions = () => {
    const queryClient = useQueryClient();
    const { data, isLoading, refetch } = useQuery({
        queryKey: ['sessions'],
        queryFn: sessionsQueryFn,
        retry: (failureCount, error) => {
            if (error && failureCount > 0) {
                toast({
                    title: 'Error',
                    description: `Failed to retrieve sessions, 
                         ${failureCount <= 3 ? 'Retrying... ' + failureCount : 'Please try again later.'}`,
                    variant: 'destructive',
                });
            }
            if (failureCount > 3) {
                queryClient.resetQueries({
                    queryKey: ['authUser'],
                    stale: true,
                });
            }
            return failureCount <= 3;
        },
    });

    const { mutate, isPending } = useMutation({
        mutationFn: revokeSessionMutationFn,
    });

    const {
        mutate: revokeAllOtherSessions,
        isPending: revokeAllOtherSessionsPending,
    } = useMutation({
        mutationFn: revokeAllsessionsMutationFn,
    });

    const sessions = data?.sessions || [];

    const currentSession = sessions?.find((session) => session.isCurrent);
    const othersSessions = sessions?.filter(
        (session) => session.isCurrent !== true,
    );

    const handleDeleteSingleSession = useCallback(
        (id: string) => {
            mutate(id, {
                onSuccess: ({ data }) => {
                    refetch();
                    toast({
                        title: 'Success',
                        description: data.message,
                    });
                },
                onError: (error) => {
                    toast({
                        title: 'Error',
                        description: error.message,
                        variant: 'destructive',
                    });
                },
            });
        },
        [mutate, refetch],
    );

    const handleDeleteAllOtherSessions = () => {
        revokeAllOtherSessions(undefined, {
            onSuccess: ({ data }) => {
                refetch();
                toast({
                    title: 'Success',
                    description: data.message,
                });
            },
            onError: (error) => {
                toast({
                    title: 'Error',
                    description: error.message,
                    variant: 'destructive',
                });
            },
        });
    };

    useEffect(() => {
        refetch();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    if (!currentSession) {
        queryClient.resetQueries({
            queryKey: ['authUser'],
            type: 'all',
        });
    }

    return (
        <div className='via-root to-root rounded-xl bg-gradient-to-r p-0.5'>
            <div className='w-full p-6'>
                <h1 className='text-fluid-h1 tracking-[-0.16px] text-slate-12 font-bold mb-1'>
                    Sessions
                </h1>
                <p className='mb-6 max-w-xl text-sm text-muted-foreground font-normal'>
                    Sessions show all the devices and browsers currently logged
                    into your account, allowing you to review and manage them.
                </p>

                {isLoading ? (
                    <div className=''>
                        <Loader
                            size='35px'
                            className='animate-spin my-32 mx-auto'
                        />
                    </div>
                ) : (
                    <div className='rounded-t-xl max-w-xl'>
                        <div>
                            <h5 className='text-base font-semibold'>
                                Current active session
                            </h5>
                            <p className='mb-6 text-[0.8rem] text-muted-foreground'>
                                You&apos;re currently logged into your account
                                here on this device / browser.
                            </p>
                        </div>
                        <div className='w-full'>
                            {currentSession && (
                                <div className='w-full py-2 border-b pb-5'>
                                    <SessionItem
                                        userAgent={currentSession.userAgent}
                                        date={currentSession.createdAt}
                                        expiresAt={currentSession.expiresAt}
                                        isCurrent={currentSession.isCurrent}
                                        ip={currentSession.ip}
                                        city={currentSession.city}
                                        country={currentSession.country}
                                    />
                                </div>
                            )}
                            <div className='mt-4'>
                                {othersSessions && othersSessions.length > 0 ? (
                                    <>
                                        <div className='flex flex-col md:flex-row justify-between items-center gap-6'>
                                            <div className='flex flex-col gap-4'>
                                                <h5 className='text-base font-semibold'>
                                                    Log out of other sessions
                                                </h5>
                                                <div className='flex flex-col gap-6 text-muted-foreground text-[0.8rem]'>
                                                    <p>
                                                        You&apos;re logged into
                                                        these accounts on these
                                                        devices and aren’t
                                                        currently using them.
                                                    </p>
                                                    <p>
                                                        Logging out will end{' '}
                                                        {othersSessions.length}{' '}
                                                        of your other active ASM
                                                        sessions. It won&apos;t
                                                        affect your current
                                                        active session.
                                                    </p>
                                                </div>
                                            </div>
                                            <Button
                                                variant={'destructive'}
                                                onClick={
                                                    handleDeleteAllOtherSessions
                                                }>
                                                Log out of all other sessions
                                            </Button>
                                        </div>
                                        <ul className='w-full mt-4 space-y-3  max-h-max overflow-y-auto'>
                                            {othersSessions?.map(
                                                (session, index) => (
                                                    <li
                                                        key={session.id}
                                                        className={`py-4 border-t-2 ${index === othersSessions.length - 1 ? 'border-b-2' : ''}`}>
                                                        <SessionItem
                                                            loading={
                                                                isPending ||
                                                                revokeAllOtherSessionsPending
                                                            }
                                                            userAgent={
                                                                session.userAgent
                                                            }
                                                            expiresAt={
                                                                session.expiresAt
                                                            }
                                                            date={
                                                                session.createdAt
                                                            }
                                                            ip={session.ip}
                                                            city={session.city}
                                                            country={
                                                                session.country
                                                            }
                                                            onRemove={handleDeleteSingleSession.bind(
                                                                null,
                                                                session.id,
                                                            )}
                                                        />
                                                    </li>
                                                ),
                                            )}
                                        </ul>
                                    </>
                                ) : (
                                    <>
                                        <span className='text-sm'>
                                            There are no other sessions
                                        </span>
                                    </>
                                )}
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default Sessions;
